import { Link, router, usePage } from "@inertiajs/react"
import { Briefcase, Check, ChevronDown } from "lucide-react"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { frontAccountantClients, frontAccountSelections } from "@/routes"

/**
 * The accountant's client next to the logo, with a menu of their clients across accounts; it takes
 * the place of the account and company switchers in the accounting app. Opening a client shows its
 * overview. The person's own accounts, where they are an owner or an employee, follow: they open
 * the company app.
 */
export function ClientSwitcher() {
  const { accountant_navigation: navigation, account_context: accounts } = usePage().props
  if (!navigation) return null

  const current = navigation.clients.find((client) => client.id === navigation.client_id) ?? null
  const ownAccounts = accounts?.all.filter((account) => !account.accounting) ?? []

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={current ? `Client: ${current.name}. Switch client` : "Choose a client"}
        className="inline-flex max-w-40 items-center gap-1.5 rounded-md px-2 py-2 text-sm font-semibold text-inverse-foreground outline-none hover:bg-inverse-hover focus-visible:ring-2 focus-visible:ring-inverse-foreground sm:max-w-56"
      >
        <Briefcase className="size-4 shrink-0 text-inverse-muted-foreground" aria-hidden="true" />
        <span className="truncate">{current ? current.name : "Choose a client"}</span>
        <ChevronDown className="size-4 shrink-0 text-inverse-muted-foreground" aria-hidden="true" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-96 w-72 overflow-y-auto">
        <DropdownMenuLabel>Switch client</DropdownMenuLabel>
        {navigation.clients.map((client) => (
          <DropdownMenuItem key={client.id} asChild>
            <Link href={frontAccountantClients.show(client.id).url} aria-current={client.id === current?.id ? "true" : undefined}>
              <span className="min-w-0 flex-1">
                <span className="block truncate">{client.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{client.account_name}</span>
              </span>
              {client.id === current?.id && <Check className="size-4" aria-label="Current client" />}
            </Link>
          </DropdownMenuItem>
        ))}
        {ownAccounts.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Your accounts</DropdownMenuLabel>
            {ownAccounts.map((account) => (
              <DropdownMenuItem key={account.id} onSelect={() => router.post(frontAccountSelections.create().url, { account_id: account.id })}>
                <span className="flex-1 truncate">{account.name}</span>
              </DropdownMenuItem>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
